import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Plus, X } from "lucide-react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Navigation } from "./Navigation";
import { EASE } from "./motion-primitives";
import {
  FieldLabel,
  FieldError,
  OptionCards,
  Segmented,
  ChipGroup,
  TextField,
  TextArea,
} from "./plan-fields";
import {
  DESTINATION_CERTAINTY,
  DATES_TYPE,
  DURATIONS,
  TRAVELLER_TYPES,
  INTERESTS,
  PACE,
  TOURIST_PREFERENCE,
  BUDGET_TIERS,
  ACCOMMODATION,
  STEPS,
  INITIAL_FORM,
} from "./plan-data";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "holideviser_trip_brief";
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Only non-sensitive trip answers are kept in local storage between visits.
// Contact details and free-text notes are never persisted.
const PERSIST_KEYS = [
  "destinationCertainty",
  "destinations",
  "origin",
  "datesType",
  "travelDates",
  "departDate",
  "returnDate",
  "duration",
  "travellerType",
  "numTravellers",
  "interests",
  "pace",
  "touristPref",
  "budget",
  "accommodation",
];

const pick = (obj, keys) =>
  keys.reduce((acc, k) => (obj[k] !== undefined ? { ...acc, [k]: obj[k] } : acc), {});

const loadForm = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return { ...INITIAL_FORM, ...pick(JSON.parse(saved), PERSIST_KEYS) };
  } catch (e) {
    /* ignore */
  }
  return INITIAL_FORM;
};

const labelOf = (options, value) => options.find((o) => o.value === value)?.label || "";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// data-testid of the control behind each validation error key (for focus management).
const FIELD_IDS = {
  destinationCertainty: "options-certainty",
  destinations: "destination-input-0",
  origin: "origin-input",
  datesType: "segmented-dates",
  departDate: "depart-date-input",
  returnDate: "return-date-input",
  duration: "segmented-duration",
  travellerType: "segmented-travellerType",
  numTravellers: "num-travellers-input",
  interests: "chips-interests",
  pace: "segmented-pace",
  touristPref: "segmented-tourist",
  name: "name-input",
  email: "email-input",
  phone: "phone-input",
  consent: "consent-checkbox",
};

export const PlanMyTrip = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(loadForm);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState("");
  const topRef = useRef(null);

  // Prefill destination when arriving from a destination page (Step 18).
  useEffect(() => {
    const dest = searchParams.get("destination");
    if (dest) {
      setForm((f) => ({ ...f, destinationCertainty: "know_exactly", destinations: [dest] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pick(form, PERSIST_KEYS)));
  }, [form]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const toggleInterest = (label) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(label)
        ? f.interests.filter((x) => x !== label)
        : [...f.interests, label],
    }));

  const setDestination = (i, val) =>
    setForm((f) => {
      const d = [...f.destinations];
      d[i] = val;
      return { ...f, destinations: d };
    });
  const addDestination = () => setForm((f) => ({ ...f, destinations: [...f.destinations, ""] }));
  const removeDestination = (i) =>
    setForm((f) => ({ ...f, destinations: f.destinations.filter((_, idx) => idx !== i) }));

  const scrollToTop = () => {
    if (window.__lenis && topRef.current) {
      window.__lenis.scrollTo(topRef.current, { offset: -110, duration: 1 });
    } else if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const focusFirstError = (e) => {
    const id = FIELD_IDS[Object.keys(e)[0]];
    if (!id) return;
    setTimeout(() => {
      let el = document.querySelector(`[data-testid="${id}"]`);
      if (el && !["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(el.tagName)) {
        el = el.querySelector("button, input, select, textarea") || el;
      }
      if (el) {
        el.focus({ preventScroll: true });
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 60);
  };

  const validateStep = (i) => {
    const e = {};
    if (i === 0) {
      if (!form.destinationCertainty) e.destinationCertainty = "Please choose one to continue.";
      if (form.destinationCertainty && form.destinationCertainty !== "not_sure") {
        if (!form.destinations.some((d) => d.trim())) e.destinations = "Add at least one place.";
      }
      if (!form.origin.trim()) e.origin = "Let us know where you're travelling from.";
    }
    if (i === 1) {
      if (!form.datesType) e.datesType = "Please select an option.";
      if (form.datesType === "exact") {
        const today = todayISO();
        if (!form.departDate) e.departDate = "Please choose your departure date.";
        else if (form.departDate < today) e.departDate = "Departure date can't be in the past.";
        if (!form.returnDate) e.returnDate = "Please choose your return date.";
        else if (form.departDate && form.returnDate < form.departDate)
          e.returnDate = "Return date can't be before departure.";
      }
      if (!form.duration) e.duration = "Please select a trip length.";
      if (!form.travellerType) e.travellerType = "Please select who's travelling.";
      if (form.numTravellers) {
        const n = Number(form.numTravellers);
        if (!Number.isInteger(n) || n < 1)
          e.numTravellers = "Please enter a whole number of travellers.";
      }
    }
    if (i === 2) {
      if (form.interests.length === 0) e.interests = "Choose at least one.";
      if (!form.pace) e.pace = "Please choose your pace.";
      if (!form.touristPref) e.touristPref = "Please choose one.";
    }
    if (i === 4) {
      if (!form.name.trim()) e.name = "Please enter your name.";
      if (!form.email.trim()) e.email = "Please enter your email.";
      else if (!isEmail(form.email)) e.email = "Please enter a valid email.";
      if (!form.phone.trim()) e.phone = "Please enter a phone or WhatsApp number.";
      if (!form.consent) e.consent = "Please tick the box so we can reach you about your trip.";
    }
    return e;
  };

  const next = () => {
    const e = validateStep(step);
    setErrors(e);
    if (Object.keys(e).length === 0) {
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
      scrollToTop();
    } else {
      focusFirstError(e);
    }
  };

  const back = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
    scrollToTop();
  };

  const submit = async () => {
    const e = validateStep(4);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      focusFirstError(e);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    const datesText =
      form.datesType === "exact"
        ? `${formatDate(form.departDate)} – ${formatDate(form.returnDate)}`
        : form.travelDates.trim() || null;
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      instagram: form.instagram.trim() || null,
      origin: form.origin.trim(),
      destinations: form.destinations.map((d) => d.trim()).filter(Boolean),
      destination_certainty: form.destinationCertainty,
      dates_type: form.datesType || null,
      travel_dates: datesText,
      trip_duration: form.duration || null,
      num_travellers: form.numTravellers ? Number(form.numTravellers) : null,
      traveller_type: form.travellerType || null,
      interests: form.interests,
      travel_pace: form.pace || null,
      tourist_preference: form.touristPref || null,
      avoid: form.avoid.trim() || null,
      budget: form.budget || null,
      accommodation_preference: form.accommodation || null,
      additional_notes: form.notes.trim() || null,
      consent: form.consent,
    };
    try {
      const res = await fetch(`${API}/api/trip-briefs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setReference(data.id || "");
      localStorage.removeItem(STORAGE_KEY);
      setSubmitted(true);
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(
        "We couldn't send your enquiry just now. Your answers are still here — please try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <Confirmation navigate={navigate} reference={reference} />;

  return (
    <div className="min-h-screen bg-ink">
      <Navigation />

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 pt-36 sm:px-10 lg:px-16 lg:pt-44">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-3xl"
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-teal" />
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">
              Plan My Trip
            </span>
          </div>
          <h1
            data-testid="plan-hero-headline"
            className="font-serif text-4xl font-light leading-[1.03] tracking-tight text-cream sm:text-5xl lg:text-6xl"
          >
            Your trip. <span className="italic text-teal">Our point of view.</span>
          </h1>
          <p className="mt-7 max-w-xl font-sans text-base leading-relaxed text-sand sm:text-lg">
            Tell us where you'd like to go, who's travelling and what matters to you. We'll review your
            brief and follow up to help shape a suitable plan.
          </p>
          <p className="mt-5 max-w-xl font-sans text-sm leading-relaxed text-sand/80">
            An enquiry starts the conversation. Availability, pricing and booking conditions are confirmed
            before you commit.
          </p>
        </motion.div>
      </section>

      {/* Questionnaire */}
      <section
        ref={topRef}
        className="mx-auto max-w-3xl px-6 pb-28 pt-16 sm:px-10 lg:pt-20"
        data-testid="questionnaire"
      >
        <Progress step={step} />

        <div className="mt-12 min-h-[360px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.4, ease: EASE }}
            >
              {step === 0 && (
                <StepShell heading="Where are you thinking of going?">
                  <OptionCards
                    name="certainty"
                    options={DESTINATION_CERTAINTY}
                    value={form.destinationCertainty}
                    onChange={(v) => set("destinationCertainty", v)}
                  />
                  <FieldError>{errors.destinationCertainty}</FieldError>

                  {form.destinationCertainty && form.destinationCertainty !== "not_sure" && (
                    <div className="mt-10">
                      <FieldLabel
                        hint="Anywhere is fine — it doesn't need to be one of our published destination guides."
                      >
                        {form.destinationCertainty === "know_exactly"
                          ? "Where would you like to go?"
                          : "Which places are you considering?"}
                      </FieldLabel>
                      <div className="flex flex-col gap-3">
                        {form.destinations.map((d, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <TextField
                              value={d}
                              onChange={(v) => setDestination(i, v)}
                              placeholder="e.g. Da Nang, Vietnam"
                              testId={`destination-input-${i}`}
                            />
                            {form.destinations.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeDestination(i)}
                                data-testid={`destination-remove-${i}`}
                                className="text-sand transition-colors hover:text-teal"
                                aria-label="Remove"
                              >
                                <X size={18} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {form.destinationCertainty === "few_places" && (
                        <button
                          type="button"
                          onClick={addDestination}
                          data-testid="destination-add"
                          className="mt-4 inline-flex items-center gap-2 font-sans text-sm text-teal transition-opacity hover:opacity-70"
                        >
                          <Plus size={15} /> Add another place
                        </button>
                      )}
                      <FieldError>{errors.destinations}</FieldError>
                    </div>
                  )}

                  {form.destinationCertainty === "not_sure" && (
                    <p
                      className="mt-6 max-w-lg font-sans text-sm leading-relaxed text-sand"
                      data-testid="undecided-hint"
                    >
                      No problem — continue and tell us what you enjoy, and we'll help you choose the
                      right destination.
                    </p>
                  )}

                  <div className="mt-10">
                    <FieldLabel>Where will you be travelling from?</FieldLabel>
                    <TextField
                      value={form.origin}
                      onChange={(v) => set("origin", v)}
                      placeholder="e.g. Mumbai, India"
                      testId="origin-input"
                    />
                    <FieldError>{errors.origin}</FieldError>
                  </div>
                </StepShell>
              )}

              {step === 1 && (
                <StepShell heading="Tell us about the trip.">
                  <FieldLabel>When are you travelling?</FieldLabel>
                  <Segmented
                    name="dates"
                    options={DATES_TYPE}
                    value={form.datesType}
                    onChange={(v) => set("datesType", v)}
                  />
                  <FieldError>{errors.datesType}</FieldError>

                  {form.datesType === "exact" && (
                    <div className="mt-6 grid gap-5 sm:grid-cols-2">
                      <div>
                        <span className="mb-2 block font-sans text-xs uppercase tracking-[0.2em] text-sand">
                          Departure
                        </span>
                        <TextField
                          type="date"
                          value={form.departDate}
                          onChange={(v) => set("departDate", v)}
                          min={todayISO()}
                          testId="depart-date-input"
                          style={{ colorScheme: "dark" }}
                        />
                        <FieldError>{errors.departDate}</FieldError>
                      </div>
                      <div>
                        <span className="mb-2 block font-sans text-xs uppercase tracking-[0.2em] text-sand">
                          Return
                        </span>
                        <TextField
                          type="date"
                          value={form.returnDate}
                          onChange={(v) => set("returnDate", v)}
                          min={form.departDate || todayISO()}
                          testId="return-date-input"
                          style={{ colorScheme: "dark" }}
                        />
                        <FieldError>{errors.returnDate}</FieldError>
                      </div>
                    </div>
                  )}

                  {form.datesType === "approximate" && (
                    <div className="mt-6">
                      <TextField
                        value={form.travelDates}
                        onChange={(v) => set("travelDates", v)}
                        placeholder="e.g. Sometime in October"
                        testId="travel-dates-input"
                      />
                    </div>
                  )}

                  <div className="mt-10">
                    <FieldLabel>How long do you want to travel?</FieldLabel>
                    <Segmented
                      name="duration"
                      options={DURATIONS}
                      value={form.duration}
                      onChange={(v) => set("duration", v)}
                    />
                    <FieldError>{errors.duration}</FieldError>
                  </div>

                  <div className="mt-10">
                    <FieldLabel>Who are you travelling with?</FieldLabel>
                    <Segmented
                      name="travellerType"
                      options={TRAVELLER_TYPES}
                      value={form.travellerType}
                      onChange={(v) => set("travellerType", v)}
                    />
                    <FieldError>{errors.travellerType}</FieldError>
                  </div>

                  <div className="mt-10">
                    <FieldLabel
                      optional
                      hint="Adults and children together. You can add the breakdown and children's ages at the time of travel in the notes on a later step."
                    >
                      Total travellers
                    </FieldLabel>
                    <div className="max-w-[200px]">
                      <TextField
                        type="number"
                        inputMode="numeric"
                        value={form.numTravellers}
                        onChange={(v) => set("numTravellers", v)}
                        placeholder="e.g. 2"
                        testId="num-travellers-input"
                        min="1"
                      />
                    </div>
                    <FieldError>{errors.numTravellers}</FieldError>
                  </div>
                </StepShell>
              )}

              {step === 2 && (
                <StepShell heading="What makes a trip feel right to you?">
                  <FieldLabel hint="Select as many as you like.">Your interests</FieldLabel>
                  <ChipGroup
                    name="interests"
                    options={INTERESTS}
                    values={form.interests}
                    onToggle={toggleInterest}
                  />
                  <FieldError>{errors.interests}</FieldError>

                  <div className="mt-10">
                    <FieldLabel hint="Choose one.">What's your ideal pace?</FieldLabel>
                    <Segmented
                      name="pace"
                      options={PACE}
                      value={form.pace}
                      onChange={(v) => set("pace", v)}
                    />
                    <FieldError>{errors.pace}</FieldError>
                  </div>

                  <div className="mt-10">
                    <FieldLabel hint="Choose one.">
                      How do you feel about touristy places?
                    </FieldLabel>
                    <Segmented
                      name="tourist"
                      options={TOURIST_PREFERENCE}
                      value={form.touristPref}
                      onChange={(v) => set("touristPref", v)}
                    />
                    <FieldError>{errors.touristPref}</FieldError>
                  </div>

                  <div className="mt-10">
                    <FieldLabel optional>What do you want to avoid?</FieldLabel>
                    <TextArea
                      value={form.avoid}
                      onChange={(v) => set("avoid", v)}
                      placeholder="Crowded attractions, early mornings, long drives, chain restaurants…"
                      testId="avoid-input"
                    />
                  </div>
                </StepShell>
              )}

              {step === 3 && (
                <StepShell heading="Your budget & preferences.">
                  <FieldLabel
                    optional
                    hint="This gives us a starting point for suitable options. We'll discuss your actual budget and what it needs to cover when we follow up."
                  >
                    What spending level feels right for this trip?
                  </FieldLabel>
                  <Segmented
                    name="budget"
                    options={BUDGET_TIERS}
                    value={form.budget}
                    onChange={(v) => set("budget", v)}
                  />

                  <div className="mt-10">
                    <FieldLabel>What kind of stay do you prefer?</FieldLabel>
                    <Segmented
                      name="accommodation"
                      options={ACCOMMODATION}
                      value={form.accommodation}
                      onChange={(v) => set("accommodation", v)}
                    />
                  </div>

                  <div className="mt-10">
                    <FieldLabel
                      optional
                      hint="Anything that would help us shape the trip — preferred areas to stay, room arrangements, dietary preferences, accessibility needs or a special occasion. You can also share the adults/children breakdown, children's ages at the time of travel and any specific budget (include the currency, whether it is per person or for the whole group, and whether flights should be included)."
                    >
                      Anything else we should know?
                    </FieldLabel>
                    <TextArea
                      rows={5}
                      value={form.notes}
                      onChange={(v) => set("notes", v)}
                      placeholder="Tell us what would make this trip feel unmistakably yours."
                      testId="notes-input"
                    />
                  </div>
                </StepShell>
              )}

              {step === 4 && (
                <StepShell heading="Review your enquiry.">
                  <EnquirySummary form={form} />

                  <div className="mt-14 flex flex-col gap-8">
                    <div>
                      <FieldLabel>Name</FieldLabel>
                      <TextField
                        value={form.name}
                        onChange={(v) => set("name", v)}
                        placeholder="Your name"
                        testId="name-input"
                        autoComplete="name"
                      />
                      <FieldError>{errors.name}</FieldError>
                    </div>
                    <div>
                      <FieldLabel>Email</FieldLabel>
                      <TextField
                        type="email"
                        inputMode="email"
                        value={form.email}
                        onChange={(v) => set("email", v)}
                        placeholder="you@email.com"
                        testId="email-input"
                        autoComplete="email"
                      />
                      <FieldError>{errors.email}</FieldError>
                    </div>
                    <div>
                      <FieldLabel hint="Include your country code, e.g. +91 for India.">
                        Phone / WhatsApp
                      </FieldLabel>
                      <TextField
                        type="tel"
                        inputMode="tel"
                        value={form.phone}
                        onChange={(v) => set("phone", v)}
                        placeholder="+91 …"
                        testId="phone-input"
                        autoComplete="tel"
                      />
                      <FieldError>{errors.phone}</FieldError>
                    </div>
                    <div>
                      <FieldLabel optional>Instagram handle</FieldLabel>
                      <TextField
                        value={form.instagram}
                        onChange={(v) => set("instagram", v)}
                        placeholder="@yourhandle"
                        testId="instagram-input"
                      />
                    </div>

                    <label
                      className="flex cursor-pointer items-start gap-4 border border-charcoal p-5"
                      data-testid="consent-label"
                    >
                      <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={(e) => set("consent", e.target.checked)}
                        data-testid="consent-checkbox"
                        className="mt-1 h-4 w-4 accent-teal"
                      />
                      <span className="font-sans text-sm leading-relaxed text-sand">
                        I'm happy for Holideviser to contact me about my trip enquiry.
                      </span>
                    </label>
                    <FieldError>{errors.consent}</FieldError>

                    <p className="font-sans text-xs leading-relaxed text-sand/80">
                      We'll use the details you share to review and follow up on this trip enquiry.
                      See our{" "}
                      <Link
                        to="/privacy"
                        data-testid="privacy-link"
                        className="text-cream underline underline-offset-4 transition-colors hover:text-teal"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </p>

                    {submitError && (
                      <p className="font-sans text-sm text-teal" data-testid="submit-error">
                        {submitError}
                      </p>
                    )}
                  </div>
                </StepShell>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="mt-12 flex items-center justify-between border-t border-charcoal pt-8">
          {step > 0 ? (
            <button
              type="button"
              onClick={back}
              data-testid="step-back"
              className="group inline-flex items-center gap-2 font-sans text-sm uppercase tracking-[0.16em] text-cream/80 transition-colors hover:text-cream"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-0.5" />
              Back
            </button>
          ) : (
            <span />
          )}

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              data-testid="step-continue"
              className="group inline-flex items-center gap-3 rounded-none bg-teal px-8 py-4 font-sans text-sm uppercase tracking-[0.18em] text-ink transition-colors duration-500 hover:bg-cream"
            >
              Continue
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="submit-brief"
              className="group inline-flex items-center gap-3 rounded-none bg-teal px-8 py-4 font-sans text-sm uppercase tracking-[0.18em] text-ink transition-colors duration-500 hover:bg-cream disabled:opacity-60"
            >
              {submitting ? "Sending your enquiry…" : "Send my trip enquiry"}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

const StepShell = ({ heading, children }) => (
  <div>
    <h2 className="mb-10 font-serif text-3xl font-light leading-tight tracking-tight text-cream sm:text-4xl">
      {heading}
    </h2>
    {children}
  </div>
);

const SummaryRow = ({ label, value }) =>
  value ? (
    <div className="flex flex-col gap-1 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8">
      <span className="shrink-0 font-sans text-xs uppercase tracking-[0.2em] text-sand">
        {label}
      </span>
      <span className="font-sans text-sm leading-relaxed text-cream/90 sm:text-right">
        {value}
      </span>
    </div>
  ) : null;

const EnquirySummary = ({ form }) => {
  const datesLabel = labelOf(DATES_TYPE, form.datesType);
  const datesValue =
    form.datesType === "exact"
      ? `${datesLabel}: ${formatDate(form.departDate)} – ${formatDate(form.returnDate)}`
      : form.datesType === "approximate"
        ? form.travelDates.trim()
          ? `${datesLabel}: ${form.travelDates.trim()}`
          : datesLabel
        : datesLabel;

  const destinationValue =
    form.destinationCertainty === "not_sure"
      ? "Not decided yet — would like help choosing"
      : form.destinations.map((d) => d.trim()).filter(Boolean).join(" · ");

  const travellers = [
    form.numTravellers ? `${form.numTravellers} total` : "",
    labelOf(TRAVELLER_TYPES, form.travellerType),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="border border-charcoal" data-testid="enquiry-summary">
      <div className="border-b border-charcoal px-6 py-4">
        <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">
          Your enquiry so far
        </span>
      </div>
      <div className="flex flex-col divide-y divide-charcoal px-6">
        <SummaryRow label="Destination" value={destinationValue} />
        <SummaryRow label="Travelling from" value={form.origin.trim()} />
        <SummaryRow label="Dates" value={datesValue} />
        <SummaryRow label="Trip length" value={labelOf(DURATIONS, form.duration)} />
        <SummaryRow label="Travellers" value={travellers} />
        <SummaryRow label="Interests" value={form.interests.join(" · ")} />
        <SummaryRow label="Pace" value={labelOf(PACE, form.pace)} />
        <SummaryRow label="Spending level" value={labelOf(BUDGET_TIERS, form.budget)} />
        <SummaryRow label="Stay" value={labelOf(ACCOMMODATION, form.accommodation)} />
        <SummaryRow label="Avoid" value={form.avoid.trim()} />
        <SummaryRow label="Notes" value={form.notes.trim()} />
        <SummaryRow label="Name" value={form.name.trim()} />
        <SummaryRow label="Email" value={form.email.trim()} />
        <SummaryRow label="Phone" value={form.phone.trim()} />
      </div>
      <div className="border-t border-charcoal px-6 py-4">
        <span className="font-sans text-xs leading-relaxed text-sand/80">
          Something to change? Use Back to edit — your answers are kept.
        </span>
      </div>
    </div>
  );
};

const Progress = ({ step }) => (
  <div data-testid="step-progress">
    {/* Desktop */}
    <div className="hidden items-center gap-8 md:flex">
      {STEPS.map((s, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <div key={s.key} className="flex items-center gap-3">
            <span
              className={`font-sans text-xs tracking-[0.16em] ${
                active ? "text-teal" : done ? "text-cream/70" : "text-sand/50"
              }`}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <span
              className={`font-sans text-xs uppercase tracking-[0.22em] ${
                active ? "text-cream" : done ? "text-cream/60" : "text-sand/50"
              }`}
            >
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
    {/* Mobile */}
    <div className="md:hidden">
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-sans text-xs uppercase tracking-[0.22em] text-cream">
          {STEPS[step].label}
        </span>
        <span className="shrink-0 font-sans text-xs tracking-[0.16em] text-teal">
          Step {step + 1} of {STEPS.length}
        </span>
      </div>
    </div>
    {/* Bar */}
    <div className="mt-5 h-px w-full bg-charcoal">
      <motion.div
        className="h-px bg-teal"
        initial={false}
        animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
        transition={{ duration: 0.5, ease: EASE }}
      />
    </div>
  </div>
);

const Confirmation = ({ navigate, reference }) => {
  const steps = [
    { no: "01", text: "We read your brief" },
    { no: "02", text: "We curate" },
    { no: "03", text: "We come back with our point of view" },
  ];
  const goDiscoverography = () => {
    navigate("/discoverography");
  };
  const goHome = () => {
    navigate("/");
  };
  return (
    <div className="min-h-screen bg-ink" data-testid="confirmation-view">
      <Navigation />
      <section className="mx-auto max-w-3xl px-6 pb-32 pt-40 sm:px-10 lg:pt-48">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-teal" />
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">
              Enquiry received
            </span>
          </div>
          <h1
            data-testid="confirmation-headline"
            className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl"
          >
            Your enquiry is in.
          </h1>
          <p className="mt-8 max-w-xl font-sans text-base leading-relaxed text-sand sm:text-lg">
            Thank you for sharing your plans. We'll review your brief and follow up using the
            contact details you provided.
          </p>
          <p
            className="mt-6 max-w-xl border-l-2 border-teal pl-5 font-sans text-sm leading-relaxed text-cream/85"
            data-testid="enquiry-reminder"
          >
            This is an enquiry, not a confirmed booking.
          </p>
          {reference && (
            <p
              className="mt-6 font-sans text-xs uppercase tracking-[0.2em] text-sand"
              data-testid="enquiry-reference"
            >
              Enquiry reference: <span className="text-cream/80">{reference}</span>
            </p>
          )}

          <div className="mt-16">
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-sand">
              What happens next
            </span>
            <div className="mt-8 flex flex-col gap-px overflow-hidden border-y border-charcoal">
              {steps.map((s) => (
                <div key={s.no} className="flex items-baseline gap-6 py-6">
                  <span className="font-serif text-2xl font-light text-teal">{s.no}</span>
                  <span className="font-serif text-2xl font-light text-cream/90 sm:text-3xl">
                    {s.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-12 max-w-xl font-serif text-xl font-light italic leading-snug text-sand">
            You won't receive an automated itinerary generated from a database.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-8">
            <button
              type="button"
              onClick={goHome}
              data-testid="back-to-home"
              className="group inline-flex items-center gap-3 border-b border-teal pb-2 font-sans text-sm uppercase tracking-[0.18em] text-cream transition-colors hover:text-teal"
            >
              Back to homepage
              <ArrowRight
                size={16}
                className="text-teal transition-transform duration-500 group-hover:translate-x-0.5"
              />
            </button>
            <button
              type="button"
              onClick={goDiscoverography}
              data-testid="explore-discoverography"
              className="group inline-flex items-center gap-3 border-b border-transparent pb-2 font-sans text-sm uppercase tracking-[0.18em] text-sand transition-colors hover:border-teal hover:text-cream"
            >
              Explore Discoverography
              <ArrowRight
                size={16}
                className="text-teal transition-transform duration-500 group-hover:translate-x-0.5"
              />
            </button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};
